// cachebuster: 1.0.15
import React from "react";
import { Route, Navigate, Routes, useLocation } from "react-router-dom";

import Auth0ProviderWithHistory from "./components/Auth0ProviderWithHistory";
// import withTranslator from "./components/hocs/withTranslator";
import AppErrorBoundary from "./components/AppErrorBoundary";
import GlobalFullPageLoader from "./components/GlobalFullPageLoader";
import FloatingWhatsAppButton from "./components/FloatingWhatsAppButton";
import Home from "./components/Home";
import { HOME_HERO_IMAGES } from "./components/HomeHeroSection";
import withHeader from "./components/hocs/withHeader";
import Auth0Callback from "./components/Auth0Callback";
import LoginRoute from "./components/LoginRoute";
import useHeadPreloadLinks from "./hooks/useHeadPreloadLinks";
import useSeoMeta from "./hooks/useSeoMeta";
import {
  GLOSSARY_INDEX_ROUTE,
  GLOSSARY_TERM_PATH,
  STATIC_PUBLIC_ROUTES_BY_KEY,
} from "./publicRouteConfig";
import createAsyncComponent from "./utils/createAsyncComponent";

// import withTranslator from "./components/hocs/withTranslator";

// import HowItWorks from "./components/HowItWorks";
// import SavingsCalculator from "./components/SavingsCalculator";
// import LearnMore from './components/LearnMore';
// import WhoWeAre from "./components/WhoWeAre";
// import Clients from "./components/Clients";
// import Projects from "./components/Projects";
// import Benefits from "./components/Benefits";
// import Login from "./components/LoginAuth0Redirect";
// import TempDashboardOverride from "./components/TempDashboardOverride";
// import Faq from "./components/Faq";
// import Contact from "./components/Contact";

const routeLoader = (id) => (
  <GlobalFullPageLoader id={id} message="Carregando..." />
);

const HOME_HERO_PRELOAD_LINKS = [
  {
    rel: "preload",
    as: "image",
    href: HOME_HERO_IMAGES.mobile900,
    imagesrcset: `${HOME_HERO_IMAGES.mobile480} 480w, ${HOME_HERO_IMAGES.mobile640} 640w, ${HOME_HERO_IMAGES.mobile768} 768w, ${HOME_HERO_IMAGES.mobile900} 900w`,
    imagesizes: "(max-width: 600px) calc(100vw - 32px), 100vw",
    type: "image/webp",
    media: "(max-width: 600px)",
    fetchpriority: "high",
  },
  {
    rel: "preload",
    as: "image",
    href: HOME_HERO_IMAGES.desktop1600,
    imagesrcset: `${HOME_HERO_IMAGES.desktop1024} 1024w, ${HOME_HERO_IMAGES.desktop1280} 1280w, ${HOME_HERO_IMAGES.desktop1600} 1600w`,
    imagesizes: "(min-width: 1600px) 1600px, calc(100vw - 32px)",
    type: "image/webp",
    media: "(min-width: 601px)",
    fetchpriority: "high",
  },
];

const PAID_MEDIA_HERO_PRELOAD_LINKS = [
  {
    rel: "preload",
    as: "image",
    href: "/paid-media/energia-por-assinatura/hero-bg-720.webp",
    type: "image/webp",
    media: "(max-width: 767px)",
    fetchpriority: "high",
  },
  {
    rel: "preload",
    as: "image",
    href: "/paid-media/energia-por-assinatura/hero-bg-1200.webp",
    type: "image/webp",
    media: "(min-width: 768px)",
    fetchpriority: "high",
  },
];

const Dashboard = createAsyncComponent(() => import("./components/Dashboard"), {
  displayName: "AsyncDashboard",
  fallback: routeLoader("dashboard-route-loading"),
});
const Settings = createAsyncComponent(() => import("./components/Settings"), {
  displayName: "AsyncSettings",
  fallback: routeLoader("settings-route-loading"),
});
const Register = createAsyncComponent(
  () => import("./components/Register-New"),
  {
    displayName: "AsyncRegister",
    fallback: routeLoader("register-route-loading"),
  },
);
const SalesPartnerRegister = createAsyncComponent(
  () => import("./components/SalesPartnerRegister"),
  {
    displayName: "AsyncSalesPartnerRegister",
    fallback: routeLoader("sales-partner-route-loading"),
  },
);
const Partners = createAsyncComponent(() => import("./components/Partners"), {
  displayName: "AsyncPartners",
  fallback: routeLoader("partners-route-loading"),
});
const ReferralProgram = createAsyncComponent(
  () => import("./components/ReferralProgram"),
  {
    displayName: "AsyncReferralProgram",
    fallback: routeLoader("referral-program-route-loading"),
  },
);
const GlossaryIndex = createAsyncComponent(
  () => import("./components/glossary/GlossaryIndex"),
  {
    displayName: "AsyncGlossaryIndex",
    fallback: routeLoader("glossary-index-route-loading"),
  },
);
const GlossaryTerm = createAsyncComponent(
  () => import("./components/glossary/GlossaryTerm"),
  {
    displayName: "AsyncGlossaryTerm",
    fallback: routeLoader("glossary-term-route-loading"),
  },
);
const LandingPageMinasGerais = createAsyncComponent(
  () => import("./components/LandingPageMinasGerais"),
  {
    displayName: "AsyncLandingPageMinasGerais",
    fallback: routeLoader("landing-mg-route-loading"),
  },
);
const LandingPageRioDeJaneiro = createAsyncComponent(
  () => import("./components/LandingPageRioDeJaneiro"),
  {
    displayName: "AsyncLandingPageRioDeJaneiro",
    fallback: routeLoader("landing-rj-route-loading"),
  },
);
const LandingPageGoias = createAsyncComponent(
  () => import("./components/LandingPageGoias"),
  {
    displayName: "AsyncLandingPageGoias",
    fallback: routeLoader("landing-go-route-loading"),
  },
);
const EnergiaPorAssinaturaLanding = createAsyncComponent(
  () => import("./components/EnergiaPorAssinaturaLanding"),
  {
    displayName: "AsyncEnergiaPorAssinaturaLanding",
    fallback: routeLoader("energia-por-assinatura-route-loading"),
  },
);
const CadastroEnergiaPorAssinaturaLanding = createAsyncComponent(
  () => import("./components/CadastroEnergiaPorAssinaturaLanding"),
  {
    displayName: "AsyncCadastroEnergiaPorAssinaturaLanding",
    fallback: routeLoader("cadastro-energia-por-assinatura-route-loading"),
  },
);
const EconomiaNaContaDeLuzLanding = createAsyncComponent(
  () => import("./components/EconomiaNaContaDeLuzLanding"),
  {
    displayName: "AsyncEconomiaNaContaDeLuzLanding",
    fallback: routeLoader("economia-na-conta-de-luz-route-loading"),
  },
);

const HomeWithHeader = withHeader(Home, { footer: true, opaque: true });
const DashboardWithHeader = withHeader(Dashboard, {
  footer: true,
  fullPage: true,
  opaque: true,
});
const SettingsWithHeader = withHeader(Settings, {
  footer: true,
  fullPage: true,
  opaque: true,
});
const RegisterWithHeader = withHeader(Register, {
  footer: true,
  fullPage: true,
  opaque: true,
});
const SalesPartnerRegisterWithHeader = withHeader(SalesPartnerRegister, {
  footer: true,
  opaque: true,
});
const PartnersWithHeader = withHeader(Partners, {
  footer: true,
  fullPage: true,
});
const ReferralProgramWithHeader = withHeader(ReferralProgram, {
  footer: true,
  fullPage: true,
});
const GlossaryIndexWithHeader = withHeader(GlossaryIndex, {
  footer: true,
  fullPage: true,
  opaque: true,
});
const GlossaryTermWithHeader = withHeader(GlossaryTerm, {
  footer: true,
  fullPage: true,
  opaque: true,
});
const LandingPageMinasGeraisWithHeader = withHeader(LandingPageMinasGerais, {
  footer: true,
  overlayHeader: true,
});
const LandingPageRioDeJaneiroWithHeader = withHeader(LandingPageRioDeJaneiro, {
  footer: true,
  overlayHeader: true,
});
const LandingPageGoiasWithHeader = withHeader(LandingPageGoias, {
  footer: true,
  overlayHeader: true,
});
const homeRouteConfig = STATIC_PUBLIC_ROUTES_BY_KEY.home;
const registerRouteConfig = STATIC_PUBLIC_ROUTES_BY_KEY.register;
const partnersRouteConfig = STATIC_PUBLIC_ROUTES_BY_KEY.partners;
const referralRouteConfig = STATIC_PUBLIC_ROUTES_BY_KEY.referral;
const salesPartnerRegisterRouteConfig =
  STATIC_PUBLIC_ROUTES_BY_KEY.sales_partner_register;
const minasGeraisLandingRouteConfig =
  STATIC_PUBLIC_ROUTES_BY_KEY["energia-mais-barata-minas-gerais"];
const rioDeJaneiroLandingRouteConfig =
  STATIC_PUBLIC_ROUTES_BY_KEY["energia-mais-barata-rio-de-janeiro"];
const goiasLandingRouteConfig =
  STATIC_PUBLIC_ROUTES_BY_KEY["energia-mais-barata-goias"];
const energiaPorAssinaturaRouteConfig =
  STATIC_PUBLIC_ROUTES_BY_KEY.energia_por_assinatura;
const cadastroEnergiaPorAssinaturaRouteConfig =
  STATIC_PUBLIC_ROUTES_BY_KEY.cadastro_energia_por_assinatura;
const economiaNaContaDeLuzRouteConfig =
  STATIC_PUBLIC_ROUTES_BY_KEY.economia_na_conta_de_luz;

const HomeRoute = () => {
  useSeoMeta(homeRouteConfig.seo);
  useHeadPreloadLinks("home-hero", HOME_HERO_PRELOAD_LINKS);
  return <HomeWithHeader />;
};

const RegisterRoute = () => {
  useSeoMeta(registerRouteConfig.seo);
  return <RegisterWithHeader />;
};

const PartnersRoute = () => {
  useSeoMeta(partnersRouteConfig.seo);
  return <PartnersWithHeader />;
};

const ReferralProgramRoute = () => {
  useSeoMeta(referralRouteConfig.seo);
  return <ReferralProgramWithHeader />;
};

const SalesPartnerRegisterRoute = () => {
  useSeoMeta(salesPartnerRegisterRouteConfig.seo);
  return <SalesPartnerRegisterWithHeader />;
};

const MinasGeraisLandingRoute = () => {
  useSeoMeta(minasGeraisLandingRouteConfig.seo);
  return <LandingPageMinasGeraisWithHeader />;
};

const RioDeJaneiroLandingRoute = () => {
  useSeoMeta(rioDeJaneiroLandingRouteConfig.seo);
  return <LandingPageRioDeJaneiroWithHeader />;
};

const GoiasLandingRoute = () => {
  useSeoMeta(goiasLandingRouteConfig.seo);
  return <LandingPageGoiasWithHeader />;
};

const EnergiaPorAssinaturaRoute = () => {
  useSeoMeta(energiaPorAssinaturaRouteConfig.seo);
  useHeadPreloadLinks("paid-media-hero", PAID_MEDIA_HERO_PRELOAD_LINKS);
  return <EnergiaPorAssinaturaLanding />;
};

const CadastroEnergiaPorAssinaturaRoute = () => {
  useSeoMeta(cadastroEnergiaPorAssinaturaRouteConfig.seo);
  useHeadPreloadLinks("paid-media-hero", PAID_MEDIA_HERO_PRELOAD_LINKS);
  return <CadastroEnergiaPorAssinaturaLanding />;
};

const EconomiaNaContaDeLuzRoute = () => {
  useSeoMeta(economiaNaContaDeLuzRouteConfig.seo);
  useHeadPreloadLinks("paid-media-hero", PAID_MEDIA_HERO_PRELOAD_LINKS);
  return <EconomiaNaContaDeLuzLanding />;
};

const App = () => {
  const location = useLocation();

  return (
    <Auth0ProviderWithHistory>
      <AppErrorBoundary resetKey={location.pathname}>
        <Routes>
          <Route exact path={homeRouteConfig.path} element={<HomeRoute />} />
          <Route path="/auth/callback" element={<Auth0Callback />} />
          <Route exact path="/login" element={<LoginRoute />} />
          <Route
            exact
            path="/dashboard"
            element={<DashboardWithHeader footer />}
          />
          <Route path="/settings/*" element={<SettingsWithHeader footer />} />
          <Route
            exact
            path={registerRouteConfig.path}
            element={<RegisterRoute />}
          />
          <Route
            exact
            path={salesPartnerRegisterRouteConfig.path}
            element={<SalesPartnerRegisterRoute />}
          />
          <Route
            exact
            path={partnersRouteConfig.path}
            element={<PartnersRoute />}
          />
          <Route
            exact
            path={referralRouteConfig.path}
            element={<ReferralProgramRoute />}
          />
          <Route
            exact
            path={minasGeraisLandingRouteConfig.path}
            element={<MinasGeraisLandingRoute />}
          />
          <Route
            exact
            path={rioDeJaneiroLandingRouteConfig.path}
            element={<RioDeJaneiroLandingRoute />}
          />
          <Route
            exact
            path={goiasLandingRouteConfig.path}
            element={<GoiasLandingRoute />}
          />
          <Route
            exact
            path={energiaPorAssinaturaRouteConfig.path}
            element={<EnergiaPorAssinaturaRoute />}
          />
          <Route
            exact
            path={cadastroEnergiaPorAssinaturaRouteConfig.path}
            element={<CadastroEnergiaPorAssinaturaRoute />}
          />
          <Route
            exact
            path={economiaNaContaDeLuzRouteConfig.path}
            element={<EconomiaNaContaDeLuzRoute />}
          />
          <Route
            exact
            path={GLOSSARY_INDEX_ROUTE.path}
            element={<GlossaryIndexWithHeader />}
          />
          <Route
            exact
            path={GLOSSARY_TERM_PATH}
            element={<GlossaryTermWithHeader />}
          />

          {/* <Route exact path="/home" component={Home} />
      <Route exact path="/how-it-works" component={HowItWorks} />
      <Route exact path="/savings-calculator" component={SavingsCalculator} />
      <Route exact path="/benefits" component={Benefits} />
      <Route exact path="/who-we-are" component={WhoWeAre} />
      <Route exact path="/clients" component={Clients} />
      <Route exact path="/projects" component={Projects} />
      <Route exact path="/faq" component={Faq} />
      <Route exact path="/contact" component={Contact} /> */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {/* To reenable this button, uncomment its import and render below, and ask Gray to disable the HubSpot widget. */}
        <FloatingWhatsAppButton
          onPaidLanding={
            location.pathname === energiaPorAssinaturaRouteConfig.path ||
            location.pathname ===
              cadastroEnergiaPorAssinaturaRouteConfig.path ||
            location.pathname === economiaNaContaDeLuzRouteConfig.path
          }
        />
      </AppErrorBoundary>
    </Auth0ProviderWithHistory>
  );
};

export default App;
